import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import * as amplitude from '@amplitude/analytics-browser';
import { useMediaQuery } from 'usehooks-ts';

import { useSwitchChainHandler } from 'modules/Room/Hooks/useSwitchHandler';
import { usePrizeBoxStore } from 'modules/prize-box-page/store/prize-box-store';

import { ConnectWallet } from 'shared/components/sections/connect-wallet/connect-wallet';
import { chainIcons } from 'shared/config';
import { ROUTES } from 'shared/constants';
import { useAccount, useSwitchChain } from 'shared/hooks/use-web3';
import { useQuestsStore } from 'shared/store/questsStore';
import { useUIStore } from 'shared/store/uiStore';
import { useWeb3ClientsStore } from 'shared/store/web3-clients-store';

import type { HomeViewProps } from './home.types';
import { DesktopView, MobileView } from './views';

export const HomePage = () => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isPrivyLoading } = useWeb3ClientsStore();

  const { isConnected, address, chain, chainId } = useAccount();
  const { chains } = useSwitchChain();
  const switchChainHandler = useSwitchChainHandler();

  const { attemptsLeft } = usePrizeBoxStore();
  const { pointBalance, activeQuests } = useQuestsStore();
  const { connectWalletModal, setConnectWalletModalOpen } = useUIStore();

  // lifted UI state
  const [networkOpen, setNetworkOpen] = useState(false);

  useEffect(() => {
    const hydrateFromUrl = () => {
      const usp = new URLSearchParams(window.location.search);
      if (usp) {
        const ampDeviceId = usp.get('ampDeviceId');
        const ampSessionId = usp.get('ampSessionId');

        if (ampDeviceId && ampSessionId) {
          amplitude.setDeviceId(ampDeviceId);
          amplitude.setSessionId(Number(ampSessionId));
        }
      }
    };
    hydrateFromUrl();
  }, []);

  const chainsList = useMemo(
    () =>
      chains.map((c) => ({
        id: c.id,
        name: c.name,
        icon: chainIcons[c.id],
        value: c.id,
        label: c.name,
      })),
    [chains],
  );
  const onChainSelect = useCallback<HomeViewProps['onChainSelect']>(
    async (id) => {
      const selectedChain = chainsList.find((c) => c.id === id);
      if (selectedChain) {
        await switchChainHandler(selectedChain);
        setNetworkOpen(false);
        if (location.pathname.startsWith(ROUTES.ROOM)) navigate(ROUTES.HOME);
      }
    },
    [switchChainHandler, navigate, chainsList],
  );

  const handleWalletConnect = useCallback<HomeViewProps['handleWalletConnect']>(
    () => setConnectWalletModalOpen(true),
    [setConnectWalletModalOpen],
  );

  const viewProps: HomeViewProps = useMemo(
    () => ({
      title: t('pages.landing.title'),
      chooseText: t('pages.landing.choose'),
      yourChainText: t('pages.landing.yourChain'),
      address: address as `0x${string}` | undefined,
      pointBalance: Number(pointBalance),
      attemptsLeft,
      activeQuests,
      currentChain: { id: chainId, name: chain?.name, icon: chainIcons[chainId ?? -1] },
      chains: chainsList,
      networkOpen,
      isConnected,
      setNetworkOpen,
      onChainSelect,
      handleWalletConnect,
    }),
    [
      t,
      address,
      pointBalance,
      attemptsLeft,
      activeQuests,
      chainId,
      chain,
      chainsList,
      networkOpen,
      isConnected,
      setNetworkOpen,
      onChainSelect,
      handleWalletConnect,
    ],
  );

  if (connectWalletModal.show && !isConnected && !isPrivyLoading) {
    return (
      <ConnectWallet title={'common.connectWallet'} description={'pages.dailyQuests.connectDesc'} />
    );
  }
  return isMobile ? <MobileView {...viewProps} /> : <DesktopView {...viewProps} />;
};
